import * as React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import useProductName from "brand-customization/useProductName";
import { Formik, Form, Field } from "formik";
import useAssistantHistory from "ai/hooks/useAssistantHistory";
import Spinner from "components/common/Spinner";
import Well from "components/bootstrap/Well";
import Button from "components/bootstrap/Button";
import Alert from "components/bootstrap/Alert";
import IconButton from "components/common/IconButton";
import ButtonGroup from "components/bootstrap/ButtonGroup";
import ClipboardIconButton from "components/common/ClipboardIconButton";
import Markdown from "components/common/Markdown";
import AIQuotaInfo from "ai/common/AIQuotaInfo";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { getPathnameWithoutId } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import OverlayTrigger from "components/common/OverlayTrigger";
import { Input } from "components/bootstrap";
import { Timestamp } from "components/common";
import {
  SidebarContainer,
  SidebarRow,
  SidebarContentArea,
  SidebarHeader,
  SidebarTitle
} from "components/layout/RightSidebar";
import useAssistantSidebar from "./useAssistantSidebar";
const SIDEBAR_WIDTH = 400;
const SidebarPanel = styled(SidebarContainer)`
  overflow: hidden;
`;
const HistoryTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const ChatBubble = styled(Well)`
  margin-left: auto;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;
const TimestampText = styled.span`
  font-size: ${({ theme }) => theme.fonts.size.small};
  white-space: nowrap;
`;
const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
const Question = ({ request, timestamp = void 0 }) => /* @__PURE__ */ React.createElement(QuestionContainer, null, /* @__PURE__ */ React.createElement(ChatBubble, null, "AI summary of ", /* @__PURE__ */ React.createElement("strong", null, request.payload.name), "."), timestamp && /* @__PURE__ */ React.createElement(TimestampText, null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timestamp, format: "dateHourAndMinute" })));
const ResponseRating = () => {
  const overlay = useRef();
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const onRating = useCallback(
    (values) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.USER_SURVEY, {
        app_pathname: getPathnameWithoutId(pathname),
        app_value: values
      });
      UserNotification.success("Thank you for your feedback!");
    },
    [pathname, sendTelemetry]
  );
  const onGoodRating = useCallback(() => onRating({ rating: 3 }), [onRating]);
  const onBadRating = useCallback(
    (rating) => {
      onRating(rating);
      overlay.current.hide();
    },
    [onRating]
  );
  const badRatingOverlay = useMemo(
    () => /* @__PURE__ */ React.createElement(Formik, { onSubmit: onBadRating, initialValues: { rating: 1, review: void 0 } }, /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Field, { name: "review" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        id: name,
        name,
        type: "textarea",
        value,
        onChange,
        label: /* @__PURE__ */ React.createElement("span", null, "What can be improved? ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")),
        rows: 4
      }
    )), /* @__PURE__ */ React.createElement(Button, { bsSize: "xs", type: "submit" }, "Submit"))),
    [onBadRating]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(IconButton, { name: "thumb_up", iconType: "regular", size: "xs", title: "Great response!", onClick: onGoodRating }), /* @__PURE__ */ React.createElement(OverlayTrigger, { ref: overlay, overlay: badRatingOverlay, placement: "bottom", trigger: "click", rootClose: true }, /* @__PURE__ */ React.createElement(IconButton, { name: "thumb_down", iconType: "regular", size: "xs", title: "Bad response!" })));
};
const ResponseActions = ({ response }) => /* @__PURE__ */ React.createElement(ButtonGroup, null, /* @__PURE__ */ React.createElement(ResponseRating, null), /* @__PURE__ */ React.createElement(ClipboardIconButton, { text: response.text, buttonTitle: "Copy response to clipboard" }));
const ResponseContainer = styled.div`
  margin-bottom: 20px;
  border-radius: 8px;
  padding: 10px;
  border: 1px solid transparent;
  animation: ${({ $highlight }) => $highlight ? "highlight-fade 5s" : "none"};

  @keyframes highlight-fade {
    from {
      border-color: ${({ theme }) => theme.colors.variant.success};
      background-color: ${({ theme }) => theme.colors.background.secondaryNav};
    }

    to {
      border-color: transparent;
      background-color: inherit;
    }
  }
`;
const Separator = styled.div`
  margin: 20px 0;
  border-bottom: 1px solid;
`;
const Response = ({ response, highlight = false }) => /* @__PURE__ */ React.createElement(ResponseContainer, { $highlight: highlight }, /* @__PURE__ */ React.createElement(Markdown, { augment: true, text: response.text }), /* @__PURE__ */ React.createElement(ResponseActions, { response }));
const AssistantEntry = ({ history, highlight = false }) => /* @__PURE__ */ React.createElement(HistoryTimeline, null, /* @__PURE__ */ React.createElement(Separator, null), /* @__PURE__ */ React.createElement(Question, { request: history.request, timestamp: history.timestamp }), /* @__PURE__ */ React.createElement(Response, { response: history.response, highlight }));
const PendingRequest = ({ pendingRequest }) => {
  if (pendingRequest === null) {
    return null;
  }
  const indicator = pendingRequest.isPending === true ? /* @__PURE__ */ React.createElement(Spinner, { text: "Generating response ..." }) : /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, "Error generating response: ", pendingRequest.error, " ", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", onClick: pendingRequest.retry }, "Click here to retry"), " ");
  return /* @__PURE__ */ React.createElement(HistoryTimeline, null, /* @__PURE__ */ React.createElement(Question, { request: pendingRequest.request }), indicator);
};
const QuotaContainer = styled.div`
  padding-left: 15px;
  padding-right: 15px;
`;
const Header = () => {
  const { close } = useAssistantSidebar();
  const productName = useProductName();
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SidebarHeader, null, /* @__PURE__ */ React.createElement(SidebarTitle, null, productName, " Assistant"), /* @__PURE__ */ React.createElement(IconButton, { name: "close", title: "Close sidebar", onClick: close, "aria-label": "Close sidebar" })), /* @__PURE__ */ React.createElement(QuotaContainer, null, /* @__PURE__ */ React.createElement(AIQuotaInfo, { text: "requests left today" })));
};
const FooterContainer = styled.div(
  ({ theme }) => css`
    padding: 10px 15px;
    font-size: ${theme.fonts.size.small};
    color: ${theme.colors.text.secondary};
  `
);
const Footer = () => /* @__PURE__ */ React.createElement(FooterContainer, null, "AI-generated summaries may occasionally contain inaccurate or misleading information. Always verify critical data independently.");
const AssistantSidebar = () => {
  const { data, isLoading } = useAssistantHistory();
  const { pendingRequest } = useAssistantSidebar();
  const messagesEndRef = useRef(null);
  useEffect(() => {
    var _a;
    if (!isLoading && (messagesEndRef == null ? void 0 : messagesEndRef.current)) {
      (_a = messagesEndRef.current) == null ? void 0 : _a.scrollIntoView({ behavior: "instant", block: "end" });
    }
  }, [isLoading]);
  return /* @__PURE__ */ React.createElement(SidebarPanel, { $width: SIDEBAR_WIDTH, role: "complementary", "aria-label": "Assistant sidebar" }, /* @__PURE__ */ React.createElement(SidebarRow, { className: "content" }, /* @__PURE__ */ React.createElement(Header, null), /* @__PURE__ */ React.createElement(SidebarContentArea, null, isLoading ? /* @__PURE__ */ React.createElement(Spinner, null) : data.map((history, idx) => (
    // eslint-disable-next-line react/no-array-index-key
    /* @__PURE__ */ React.createElement(AssistantEntry, { key: idx, history, highlight: idx === data.length - 1 && !pendingRequest })
  )), !isLoading && data.length === 0 && !pendingRequest && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "Your history is currently empty."), pendingRequest && /* @__PURE__ */ React.createElement(PendingRequest, { pendingRequest }), /* @__PURE__ */ React.createElement("div", { ref: messagesEndRef })), /* @__PURE__ */ React.createElement(Footer, null)));
};
const AISidebarWrapper = () => {
  const { isOpen } = useAssistantSidebar();
  return isOpen ? /* @__PURE__ */ React.createElement(AssistantSidebar, null) : null;
};
export default AISidebarWrapper;
